import { v1 as uuid } from 'uuid';

import { createConfigParametersRepository } from '../../configParametersRepository';
import { REMEMBER_ME_COOKIE_NAME } from '../resources/constants';
import { datalayerStorageClient } from './datalayerStorage';
import type { Primitive } from './types';

export type FlattenedObject<T extends Record<string, unknown>> = {
  [K in keyof T & string]: Primitive;
};
/**
 * Flattens a nested object into a single level object.
 * @param obj - The nested object.
 * @returns {FlattenedObject<T>} - The flattened object.
 */
export const flatten = <T extends Record<string, unknown>>(
  obj: T = {} as T
): FlattenedObject<T> => {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key as keyof T];
    if (
      Object.prototype.toString.call(value) === '[object Object]' &&
      value !== null
    ) {
      return {
        ...acc,
        ...flatten(value as Record<string, object | Primitive>),
      };
    }
    return {
      ...acc,
      [key]: isNilOrEmpty(value as Primitive) ? '' : `${value as Primitive}`,
    };
  }, {} as FlattenedObject<T>);
};

/**
 * Creates a diff object given two objects of the same type.
 * @param obj1 - The first object.
 * @param obj2 - The second object.
 * @returns {Partial<T>} - The diff object containing only the properties that have different values.
 */
export function diff<T extends Record<string, Primitive>>(
  obj1: T,
  obj2: Partial<T>
): Partial<T> {
  const result: Partial<T> = {};
  Object.keys(obj1).forEach((key) => {
    if (obj1[key] !== obj2[key]) {
      (result as Record<string, Primitive>)[key] = obj1[key];
    }
  });
  return result;
}

/**
 * Merges a Datalayer[] in a Datalayer single object
 * @returns {Datalayer}
 */
export function mergeLayers<T extends Record<string, unknown>>(layers: T[]): T {
  if (!layers || !layers.length) {
    return {} as T;
  }
  return layers.reduce((acc, layer) => {
    Object.keys(layer).forEach((key) => {
      (acc as Record<string, unknown>)[key] = layer[key];
    });
    return acc;
  }, {} as T);
}

/**
 * Generates a unique conversion identifier.
 * The identifier is composed of a UUID and the current timestamp, separated by a dot.
 * @returns {string} A unique conversion identifier in the format `UUID.timestamp`.
 */
export const getConvId = () => {
  return `${uuid()}.${new Date().getTime()}`;
};

/**
 * Checks if a value is null, undefined, an empty string, or NaN (for numbers).
 * @param value - The value to check.
 * @returns {boolean} - True if the value is null, undefined, an empty string, or NaN; otherwise, false.
 */
export const isNilOrEmpty = (value: Primitive): boolean => {
  return (
    value == null || // Covers both null and undefined
    (typeof value === 'string' && value.trim() === '') ||
    (typeof value === 'number' && isNaN(value))
  );
};

/**
 * Creates an empty object with keys from the provided array, initializing each key with an empty string.
 * This is useful for creating a structured object where all values are initially empty.
 * @param keys - An array of strings representing the keys for the object.
 * @template T - The type of the object to be created.
 * @returns T - An object of type T with keys from the array and empty string values.
 */
export const createEmptyObjectFromArray = <T extends Record<string, string>>(
  keys: Array<string>
): T => {
  return keys.reduce(
    (acc, item) => ({
      ...acc,
      [item]: '',
    }),
    {} as T
  );
};

/**
 * Retrieves the value of the "remember me" cookie from the tracking storage client.
 * @returns {string} The value of the "remember me" cookie, or an empty string if not found.
 */
export const getRememberMeCookie = () => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  return storageClient?.getCookie(REMEMBER_ME_COOKIE_NAME) || '';
};

/**
 * Retrieves a parameter from the current or previous datalayer.
 * @param {string} parameterName - name of param to retrieve
 * @returns {string} value of the parameter or empty string
 */
export const getParamFromDatalayer = (parameterName: string): string => {
  const { getPreviousDatalayer } = datalayerStorageClient();
  const tcVars = window?.tc_vars as
    | Record<string, string | undefined>
    | null
    | undefined;
  const previousDatalayer = getPreviousDatalayer() as Record<
    string,
    string | undefined
  >;
  return tcVars?.[parameterName] || previousDatalayer[parameterName] || '';
};
